import { FormEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { t } from '../hooks/useT';
import { useAuth } from '../hooks/useAuth';
import { NOTIFY_HOUR_OPTIONS, usePushNotifications } from '../hooks/usePushNotifications';

export function ProfileNotificationsSection() {
  const { user, updateNotificationPrefs } = useAuth();
  const push = usePushNotifications();
  const [params] = useSearchParams();
  const sectionRef = useRef<HTMLElement>(null);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [notifyTime, setNotifyTime] = useState('08:00');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    setEmailEnabled(!!user.notifyEmailEnabled);
    setPushEnabled(!!user.notifyPushEnabled);
    setNotifyTime(user.notifyTime || '08:00');
  }, [user]);

  useEffect(() => {
    if (params.get('notifications') === '1' && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [params]);

  if (!user) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    setError('');
    try {
      let pushActive = pushEnabled;
      if (pushEnabled) {
        if (push.support === 'denied') {
          setError(t('notifications.pushDeniedHint'));
          setBusy(false);
          return;
        }
        if (!push.isSubscribed) {
          await push.subscribe();
        }
        pushActive = true;
      } else if (push.isSubscribed) {
        await push.unsubscribe();
      }

      await updateNotificationPrefs({
        notifyEmailEnabled: emailEnabled,
        notifyPushEnabled: pushActive,
        notifyTime,
      });
      setMsg(t('notifications.saved'));
    } catch (err) {
      const code = (err as Error).message;
      if (code === 'permission_denied') {
        setError(t('notifications.pushDenied'));
      } else {
        setError(t('notifications.saveError'));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleEnablePush = async () => {
    setError('');
    try {
      await push.subscribe();
      setPushEnabled(true);
      setMsg(t('notifications.pushEnabled'));
    } catch (err) {
      const code = (err as Error).message;
      if (code === 'permission_denied') {
        setError(t('notifications.pushDenied'));
      } else {
        setError(t('notifications.pushError'));
      }
    }
  };

  return (
    <section ref={sectionRef} className="profile-notifications" id="notifications">
      <h2 className="profile-section-title">{t('notifications.profileTitle')}</h2>
      <p className="profile-notifications-hint">{t('notifications.profileHint')}</p>

      <form className="profile-notifications-form" onSubmit={handleSubmit}>
        <label className="notification-toggle">
          <input
            type="checkbox"
            checked={emailEnabled}
            onChange={(e) => setEmailEnabled(e.target.checked)}
          />
          <span>{t('notifications.channelEmail')}</span>
        </label>

        <label className={`notification-toggle${push.support === 'unsupported' ? ' is-disabled' : ''}`}>
          <input
            type="checkbox"
            checked={pushEnabled}
            disabled={push.support === 'unsupported'}
            onChange={(e) => setPushEnabled(e.target.checked)}
          />
          <span>{t('notifications.channelPush')}</span>
        </label>

        {pushEnabled && push.support === 'default' && !push.isSubscribed && (
          <button type="button" className="btn secondary admin-btn-sm" onClick={() => void handleEnablePush()}>
            {t('notifications.enablePushButton')}
          </button>
        )}

        {push.support === 'denied' && (
          <p className="notification-note muted">{t('notifications.pushDeniedHint')}</p>
        )}

        <label className="notification-time-field">
          <span>{t('notifications.timeLabel')}</span>
          <select value={notifyTime} onChange={(e) => setNotifyTime(e.target.value)}>
            {NOTIFY_HOUR_OPTIONS.map((hour) => (
              <option key={hour} value={hour}>{hour}</option>
            ))}
          </select>
        </label>

        <p className="notification-note muted">{t('notifications.timezoneHint')}</p>

        <button type="submit" className="btn primary admin-btn-sm" disabled={busy}>
          {busy ? t('common.loading') : t('notifications.save')}
        </button>
      </form>

      {msg && <p className="admin-msg admin-msg--compact">{msg}</p>}
      {error && <p className="admin-msg admin-msg-error">{error}</p>}
    </section>
  );
}
